// Test de Denver II (028) — Serie 028, Fase 2, ver PLAN.md /
// packages/hce-chart/tasks/investigacion-028-aiepi-denver.md. Fuente
// primaria: páginas 114-115 del PDF oficial del Anexo 1 (AM-00115-2021),
// cita legal impresa en el propio formulario: `MSP/DNEAIS-HCU-form.028/mayo/2015`.
// Los 106 ítems y su franja/orden se transcribieron directamente de esa
// fuente — es la adaptación oficial ecuatoriana del Test de Denver II
// (Denver Developmental Materials Inc.), citando la norma que lo adopta, no
// una tabla de otro editor (decisión ya tomada, ver PLAN.md).
//
// **Limitación conocida, a verificar por un clínico antes de uso real**: la
// fuente es un formulario impreso escaneado — se pudo leer con confianza el
// TEXTO de cada ítem, su franja de desarrollo y su orden relativo (la
// posición en la escalera diagonal no deja ambigüedad de orden). No se pudo
// medir con precisión de píxel el rango exacto de percentil 25-90 de cada
// ítem (la barra sombreada de cada casillero) — `edadMeses` acá es UNA sola
// edad aproximada por ítem (equivalente al borde izquierdo/percentil 25,
// interpolada entre las marcas del eje que sí se leyeron con confianza), no
// el rango completo de la barra oficial. Antes de usar esto para clasificar
// a un paciente real, un profesional debería comparar el resultado en
// pantalla contra el formulario impreso oficial.
export type FranjaDenver = 'personal-social' | 'motor-fino' | 'lenguaje' | 'motor-grueso';

export interface ItemDenver {
  readonly id: string;
  readonly texto: string;
  readonly franja: FranjaDenver;
  /** Referencia a `INSTRUCCIONES_DENVER[nota]` — instrucción especial de administración (1-31, íntegras en la fuente). */
  readonly nota?: number;
  /** "R" en el original: puede pasarse preguntando al cuidador, no hace falta observarlo directamente. */
  readonly reporte?: boolean;
  /** Edad aproximada (meses) del borde izquierdo del ítem en el eje oficial — ver limitación arriba. */
  readonly edadMeses: number;
}

/** Las 31 instrucciones de administración (página 115 del PDF oficial) — texto verbatim, completamente legible en la fuente. */
export const INSTRUCCIONES_DENVER: Readonly<Record<number, string>> = {
  1: 'Intente animarle al niño a sonreír, hablarle, o saludarle con la mano.',
  2: 'El niño debe fijar la vista por varios segundos.',
  3: 'El familiar debe guiar el cepillo de dientes y aplicar la pasta.',
  4: 'El niño se viste sin ayuda (se abotona).',
  5: 'Fija y sigue con la mirada un objeto.',
  6: 'Agarra el chinesco.',
  7: 'Buscar el objeto y alcanzarlo.',
  8: 'El niño debe pasar el cubo de una mano a otra, sin la ayuda de su cuerpo, su boca.',
  9: 'Recoge objetos con el dedo pulgar (pinza).',
  10: 'Garabatea espontáneamente.',
  11: 'Haga un puño con el dedo gordo arriba y menee solamente el gordo, pase si el niño lo imita y no mueve cualquier otro dedo que no sea el gordo.',
  12: 'Pase cualquier forma encerrada. No pase movimientos continuos y redondos.',
  13: '¿Cuál línea es más larga? (no más grande) Voltee el papel y repita. (Pase 3 de 3 o 5 de 6)',
  14: 'Pase cualquier par de líneas que cruzan cerca del punto mediano.',
  15: 'El niño debe copiar primero. Si no pasa, demuéstrelo. (Al administrar puntos 12, 14 y 15, no nombre las formas. No haga demostración de 12 y 14.)',
  16: 'Dibujar tres partes de una persona.',
  17: 'Responde a sonido.',
  18: 'Indique el dibujo y dígale al niño que lo nombre (no se da crédito simplemente por decir los sonidos que hacen). Si el niño nombra menos de 4 dibujos, el examinador puede decir el nombre del animal y el niño debe indicar cada uno.',
  19: 'Usando una muñeca, dígale al niño: "Muéstrame nariz, ojos, orejas, boca, manos, pies, estómago, pelo". Pase 6 de 8.',
  20: 'Usando dibujos pregúntele: "¿Cuál vuela?...dice miau?...habla?...galopa?" Pase 2 de 5, 4 de 5.',
  21: 'Pregúntele: "¿Qué haces cuando tienes frío?...estás cansado?...tienes hambre?" Pase 2 de 3, 3 de 3.',
  22: 'Pregúntele: "¿Qué haces con una taza? ¿Para qué se usa una silla? ¿Para qué se usa un lápiz?" Se deben incluir palabras de acción.',
  23: 'Pasa si el niño coloca correctamente y dice cuántos cubos están en el papel. (1,5).',
  24: 'Dígale: "Coloca el cubo en la mesa; debajo de la mesa; delante de mí; detrás de mí" Pase 4 de 4.',
  25: 'Pregúntele: "¿Qué es una pelota?...lago?...mesa?...casa?...guineo?...cobija?...puerta?...techo?" Pase si se define en términos de uso, forma, de qué se hace o de categoría (ejemp. El guineo es una fruta, no simplemente amarillo). Pase 5 de 8.',
  26: 'Pregúntele: "Si un caballo es grande, ¿un ratón es...? Si el fuego está caliente el hielo está...? Si el sol brilla durante el día la luna durante...?" Pase 2 de 3.',
  27: 'Sube gradas.',
  28: 'Patea la pelota.',
  29: 'Salta en su lugar, salta hacia adelante, se balancea en un pie dos segundos.',
  30: 'Marcha punta talón hacia adelante.',
  31: 'Se balancea en un pie 6 segundos.',
};

/** Ejes de referencia del gráfico oficial (meses) — el tramo 0-24 meses ocupa más espacio proporcional que el tramo 24-72 (2-6 años), igual que el original impreso. */
const EJE_TICKS_MESES = [0, 2, 4, 6, 9, 12, 15, 18, 24, 36, 48, 60, 72] as const;
const EJE_POSICIONES = [0, 0.081, 0.163, 0.244, 0.326, 0.407, 0.489, 0.57, 0.57, 0.678, 0.785, 0.893, 1] as const;
// (el índice 7 y 8 comparten la marca de 24 meses — fin del tramo de meses / inicio del tramo de años)

function interpolar(x: number, xs: readonly number[], ys: readonly number[]): number {
  if (x <= xs[0]!) return ys[0]!;
  if (x >= xs[xs.length - 1]!) return ys[ys.length - 1]!;
  for (let i = 0; i < xs.length - 1; i++) {
    if (x >= xs[i]! && x <= xs[i + 1]!) {
      const t = (x - xs[i]!) / (xs[i + 1]! - xs[i]!);
      return ys[i]! + t * (ys[i + 1]! - ys[i]!);
    }
  }
  return ys[ys.length - 1]!;
}

/** Meses → posición [0,1] en el eje del gráfico. */
export function mesesAPosicionEje(meses: number): number {
  return interpolar(meses, EJE_TICKS_MESES, EJE_POSICIONES);
}

/** Posición [0,1] en el eje → meses (inversa de `mesesAPosicionEje`, para ubicar ítems por índice ordinal dentro de una franja). */
function posicionEjeAMeses(posicion: number): number {
  return interpolar(posicion, EJE_POSICIONES, EJE_TICKS_MESES);
}

/** Distribuye `cantidad` ítems uniformemente en posición-de-eje entre `desdeMeses` y `hastaMeses` — usado para construir el catálogo a partir del orden real transcrito (ver limitación en el header del archivo). */
function distribuirEdades(cantidad: number, desdeMeses: number, hastaMeses: number): number[] {
  const posDesde = mesesAPosicionEje(desdeMeses);
  const posHasta = mesesAPosicionEje(hastaMeses);
  return Array.from({ length: cantidad }, (_, i) => Math.round(posicionEjeAMeses(posDesde + (i / Math.max(cantidad - 1, 1)) * (posHasta - posDesde))));
}

/** Arma los ítems de una franja a partir del texto en orden real (edad creciente) + edades interpoladas. */
function franjaDesde(
  franja: FranjaDenver,
  prefijo: string,
  desdeMeses: number,
  hastaMeses: number,
  entradas: readonly [texto: string, opts?: { nota?: number; reporte?: boolean }][],
): ItemDenver[] {
  const edades = distribuirEdades(entradas.length, desdeMeses, hastaMeses);
  return entradas.map(([texto, opts], i) => ({
    id: `${prefijo}-${i + 1}`,
    texto,
    franja,
    nota: opts?.nota,
    reporte: opts?.reporte,
    edadMeses: edades[i]!,
  }));
}

const R = { reporte: true } as const;

/**
 * El papel imprime DOS veces "22 Uso de 2 objetos" en Lenguaje: una antes y otra después de "23 Contar cubos"
 * (pág. 114 del PDF oficial). Los ids son posicionales (`le-N`) y ya hay evaluaciones guardadas con ellos, así que
 * la segunda aparición se agrega con un id propio (`le-28b`) sin renumerar nada, y su edad queda entre la de
 * "Contar cubos" y la del ítem siguiente para conservar el orden de edad creciente.
 */
function conUsoDeObjetosBis(items: readonly ItemDenver[]): ItemDenver[] {
  const i = items.findIndex((it) => it.texto === 'Contar cubos');
  const previo = items[i]!;
  const siguiente = items[i + 1]!;
  const bis: ItemDenver = { id: 'le-28b', texto: 'Uso de 2 objetos', franja: 'lenguaje', nota: 22, edadMeses: Math.round((previo.edadMeses + siguiente.edadMeses) / 2) };
  return [...items.slice(0, i + 1), bis, ...items.slice(i + 1)];
}

export const CATALOGO_ITEMS_DENVER: readonly ItemDenver[] = [
  ...franjaDesde('personal-social', 'ps', 0, 42, [
    ['Mirar cara'],
    ['Sonreír espontáneamente'],
    ['Responder con sonrisa', { nota: 1 }],
    ['Mirarse las manos', { nota: 2, ...R }],
    ['Alcanzar juguete'],
    ['Darse/comer', R],
    ['Indicar deseos', R],
    ['Aplaudir', R],
    ['Saludar adiós', R],
    ['Jugar pelota'],
    ['Imitar actividades', R],
    ['Beber en una taza', R],
    ['Dar de comer a muñeca'],
    ['Quitarse la ropa', R],
    ['Usar cuchara/tenedor', R],
    ['Ayudar en casa', R],
    ['Lavarse/secarse manos', R],
    ['Cepillarse dientes con ayuda', { nota: 3, ...R }],
    ['Ponerse la ropa'],
    ['Ponerse camiseta', R],
    ['Nombrar amigo'],
    ['Cepillarse dientes sin ayuda', R],
    ['Jugar juegos/naipes', R],
    ['Vestirse sin ayuda', { nota: 4, ...R }],
    ['Preparar cereal', R],
  ]),
  ...franjaDesde('motor-fino', 'mf', 1, 60, [
    ['Seguir hasta línea media', { nota: 5 }],
    ['Seguir línea media', { nota: 5 }],
    ['Manos juntas'],
    ['Agarrar sonajero', { nota: 6 }],
    ['Mirar una pasa'],
    ['Seguir 180°', { nota: 5 }],
    ['Alcanzar'],
    ['Buscar el hilo', { nota: 7 }],
    ['Rastrillar pasa'],
    ['Pasar cubo', { nota: 8 }],
    ['Agarrar 2 cubos'],
    ['Apretón gordo/dedos', { nota: 9 }],
    ['Golpear 2 cubos, manos', R],
    ['Meter cubo en taza'],
    ['Garabatear'],
    ['Botar pasa, demostrado'],
    ['Torre de 2 cubos'],
    ['Torre de 4 cubos'],
    ['Torre de 6 cubos'],
    ['Torre de 8 cubos'],
    ['Imitar línea vertical', { nota: 10 }],
    ['Menear gordo', { nota: 11 }],
    ['Copiar círculo', { nota: 12 }],
    ['Dibuja persona 3 partes', { nota: 16 }],
    ['Copiar cruz', { nota: 14 }],
    ['Escoger línea más larga', { nota: 13 }],
    ['Copiar cuadrado, demostrar', { nota: 15 }],
    ['Dibujar persona 6 partes', { nota: 15 }],
    ['Copiar cuadrado', { nota: 15 }],
  ]),
  ...conUsoDeObjetosBis(franjaDesde('lenguaje', 'le', 0, 66, [
    ['Responder a campana'],
    ['Vocalizar', R],
    ['Ooo/aaa', R],
    ['Reírse', R],
    ['Chillar', R],
    ['Volver hacia sonajero', { nota: 17 }],
    ['Volver hacia voz'],
    ['Sílabas sencillas', R],
    ['Imitar sonidos del lenguaje', R],
    ['Papá/mamá no específicos', R],
    ['Sílabas combinadas', R],
    ['Ga ge gi', R],
    ['Papá/mamá específicos', R],
    ['Una palabra', R],
    ['2 palabras', R],
    ['3 palabras', R],
    ['6 palabras', R],
    ['Combinar palabras', R],
    ['Indicar 2 dibujos', { nota: 18 }],
    ['Nombrar 1 dibujo', { nota: 18 }],
    ['Partes/cuerpos', { nota: 19 }],
    ['Indicar 4 dibujos', { nota: 18 }],
    ['Habla medio-entendible'],
    ['Nombrar 4 dibujos', { nota: 18 }],
    ['Saber 2 acciones', { nota: 20 }],
    ['Saber 2 adjetivos', { nota: 21 }],
    ['Nombrar 1 color'],
    ['Uso de 2 objetos', { nota: 22 }],
    ['Contar cubos', { nota: 23 }],
    ['Saber 4 acciones', { nota: 20 }],
    ['Habla todo-entendible'],
    ['Entender 4 preposiciones', { nota: 24 }],
    ['Nombrar 4 colores'],
    ['Definir 5 palabras', { nota: 25 }],
    ['Saber 3 adjetivos', { nota: 21 }],
    ['Contar 5 cubos', { nota: 23 }],
    ['Contrarios - 2', { nota: 26 }],
    ['Definir 7 palabras', { nota: 25 }],
  ])),
  ...franjaDesde('motor-grueso', 'mg', 0, 72, [
    ['Movimientos iguales'],
    ['Levantarse cabeza', R],
    ['Cabeza levantada 45°'],
    ['Cabeza levantada 90°'],
    ['Sentarse, cabeza firme'],
    ['Pesarse en piernas'],
    ['Pecho arriba, apoyo en brazos'],
    ['Voltearse', R],
    ['Levantar a sentar, no retraso/cabeza'],
    ['Sentarse sin apoyo'],
    ['De pie, apoyándose en algo'],
    ['Ponerse de pie'],
    ['Sentarse de otra posición', R],
    ['De pie - 2 segs'],
    ['De pie - solo'],
    ['Agacharse, encoger'],
    ['Caminar bien'],
    ['Caminar para atrás', R],
    ['Correr'],
    ['Subir escalera', { nota: 27, ...R }],
    ['Pelota hacia delante'],
    ['Saltar'],
    ['Tirar pelota sobre mano'],
    ['Salto de longitud', { nota: 29 }],
    ['Equilibrio cada pie 1 segundo'],
    ['Equilibrio cada pie 2 segundos'],
    ['Saltar 1 pie'],
    ['Equilibrio cada pie 3 segundos'],
    ['Equilibrio cada pie 4 segundos'],
    ['Equilibrio cada pie 5 segundos'],
    ['Caminar talón/dedo', { nota: 30 }],
    ['Equilibrio cada pie 6 segundos'],
  ]),
];

export const FRANJAS_DENVER: readonly { readonly clave: FranjaDenver; readonly etiqueta: string }[] = [
  { clave: 'personal-social', etiqueta: 'Personal-Social' },
  { clave: 'motor-fino', etiqueta: 'Motor Fino-Adaptativo' },
  { clave: 'lenguaje', etiqueta: 'Lenguaje' },
  { clave: 'motor-grueso', etiqueta: 'Motor Grueso' },
];

export type ResultadoItemDenver = 'aprobado' | 'fallado' | 'rehuso' | 'sin-oportunidad';
export type ClasificacionDenver = 'normal' | 'dudoso' | 'anormal';

export const CLASIFICACIONES_DENVER: readonly { readonly clave: ClasificacionDenver; readonly etiqueta: string }[] = [
  { clave: 'normal', etiqueta: 'Normal' },
  { clave: 'dudoso', etiqueta: 'Dudoso' },
  { clave: 'anormal', etiqueta: 'Anormal' },
];

export interface EntradaDenverPdf {
  readonly itemId: string;
  readonly resultado: ResultadoItemDenver;
}

/** Wire format serializable de `EspecificacionPdf` (modo 'msp') — lista dispersa (solo ítems evaluados) + edad y clasificación del profesional. */
export interface DatosDenverPdf {
  readonly entradas: readonly EntradaDenverPdf[];
  readonly edadEvaluacionMeses?: number;
  /** Encabezado del papel: "Edad gestacional al nacimiento" (semanas). */
  readonly edadGestacionalSemanas?: number;
  readonly clasificacionPresuntiva?: ClasificacionDenver;
}

// ----------------------------------------------------------------------------
// Dibujo en el PDF firmado — vía `CursorPdf.dibujo()` (packages/hce-pdf/src/layout.ts),
// mismo mecanismo que el odontograma (033). A diferencia de la grilla FDI
// (32 piezas, cabe en una página carta), la cuadrícula completa de Denver II
// (106 ítems en 4 franjas diagonales) no entra de forma legible en el ancho
// disponible de una página carta (504px) — se optó por un RESUMEN de texto
// agrupado por franja (solo los ítems evaluados, con su resultado) en vez de
// redibujar la escalera diagonal en miniatura ilegible. La cuadrícula
// interactiva completa vive en la pantalla (`DenverIIVisual.tsx`), que es
// donde el profesional realmente administra la prueba — el PDF documenta el
// resultado de la evaluación, no reproduce el instrumento de tamizaje.
import { rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { BORDE_SUTIL, GRIS_OSCURO, INK } from './layout';

const COLOR_APROBADO = rgb(0x2b / 255, 0x6a / 255, 0x39 / 255);
const COLOR_FALLADO = rgb(0xb5 / 255, 0x35 / 255, 0x35 / 255);

function colorDeResultado(resultado: ResultadoItemDenver) {
  if (resultado === 'aprobado') return COLOR_APROBADO;
  if (resultado === 'fallado') return COLOR_FALLADO;
  return GRIS_OSCURO;
}

function etiquetaResultado(resultado: ResultadoItemDenver): string {
  switch (resultado) {
    case 'aprobado':
      return 'Aprobado';
    case 'fallado':
      return 'Fallado';
    case 'rehuso':
      return 'Rehusó';
    case 'sin-oportunidad':
      return 'Sin oportunidad';
  }
}

/** Encabezado del papel: "Edad cronológica" en casillas Años / Meses, derivadas de la edad en meses de la evaluación. */
export function formatearEdadCronologicaDenver(meses: number): string {
  const total = Math.max(0, Math.round(meses));
  const anios = Math.floor(total / 12);
  const resto = total % 12;
  return `${anios} ${anios === 1 ? 'año' : 'años'} ${resto} ${resto === 1 ? 'mes' : 'meses'}`;
}

/** Espacio vertical a reservar con `cursor.dibujo(ALTO_DIBUJO_DENVER(datos), ...)` — varía con la cantidad de ítems evaluados. */
export function altoDibujoDenver(datos: DatosDenverPdf): number {
  const porFranja = new Map(FRANJAS_DENVER.map((f) => [f.clave, datos.entradas.filter((e) => CATALOGO_ITEMS_DENVER.find((i) => i.id === e.itemId)?.franja === f.clave)]));
  let alto = 40; // título + clasificación + edad
  for (const [, entradas] of porFranja) {
    if (entradas.length === 0) continue;
    alto += 16 + entradas.length * 12;
  }
  return alto;
}

/** Dibuja el resumen de resultados sobre una `PDFPage` cruda, entregada por `CursorPdf.dibujo()`. Pura: no toca I/O, solo `pdf-lib`. */
export function dibujarDenver(pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont, datos: DatosDenverPdf): void {
  const porItem = new Map(CATALOGO_ITEMS_DENVER.map((i) => [i.id, i] as const));
  let y = origen.yTop - 12;

  pagina.drawText('Test de Denver II — resultados', { x: origen.x, y, size: 9, font: fontRegular, color: GRIS_OSCURO });
  y -= 16;

  const detalle = [
    datos.edadGestacionalSemanas !== undefined ? `Edad gestacional al nacimiento: ${datos.edadGestacionalSemanas} semanas` : '',
    datos.edadEvaluacionMeses !== undefined ? `Edad cronológica: ${formatearEdadCronologicaDenver(datos.edadEvaluacionMeses)}` : '',
    datos.clasificacionPresuntiva ? `Diagnóstico presuntivo: ${CLASIFICACIONES_DENVER.find((c) => c.clave === datos.clasificacionPresuntiva)?.etiqueta}` : '',
  ]
    .filter(Boolean)
    .join('   ·   ');
  if (detalle) {
    pagina.drawText(detalle, { x: origen.x, y, size: 8, font: fontRegular, color: INK });
    y -= 16;
  }

  for (const franja of FRANJAS_DENVER) {
    const entradas = datos.entradas.filter((e) => porItem.get(e.itemId)?.franja === franja.clave);
    if (entradas.length === 0) continue;
    pagina.drawLine({ start: { x: origen.x, y: y + 4 }, end: { x: origen.x + anchoDisponible, y: y + 4 }, thickness: 0.5, color: BORDE_SUTIL });
    pagina.drawText(franja.etiqueta, { x: origen.x, y, size: 8, font: fontRegular, color: GRIS_OSCURO });
    y -= 14;
    for (const entrada of entradas) {
      const item = porItem.get(entrada.itemId);
      if (!item) continue;
      pagina.drawText(`${item.texto} — ${etiquetaResultado(entrada.resultado)}`, { x: origen.x + 8, y, size: 8, font: fontRegular, color: colorDeResultado(entrada.resultado) });
      y -= 12;
    }
  }
}
